import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Form as FormCh } from "react-bootstrap";
import { Formik, Form } from "formik";
import CategoryInput from "./CategoryInput";
import {
    useCreateServiceCategoryMutation,
    useGetCurrencySignQuery,
    useUpdateServiceCategoryModalMutation,
} from "@me-team/host/main/modalSalary/graphql/modalSalary.hooks";
import SalaryComponent from "./SalaryComponent/SalaryComponent";
import { ServiceCategorySalaryProxy } from "@me-team/host/main/graphql/types";
import {
    validateSalarySections,
    processDeleteState,
    processSalarySection,
    SalarySection,
    transformSalarySections,
    FormValues,
} from "./salaryUtils";
import { useReactiveVar } from "@apollo/client";
import { TFunction } from "i18next";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import { SpinnerLoader } from "@services/ui-components";
import { useGetServicePriceLazyQuery } from "@me-team/host/main/salary/graphql/salary.hooks";

interface SalaryModalProps {
    show: boolean;
    setShowSalaryModal: React.Dispatch<React.SetStateAction<boolean>>;
    employeeId: any;
    refetch: () => void;
    t: TFunction;
    toggleShowToast?: () => void;
    accessMode?: "hidden" | "view" | "edit";
}
const defaultSalarySection: SalarySection = {
    id: Date.now(),
    categoryId: null,
    salary: null,
    salaryType: "%",
    categoryName: "",
    subCategoryId: null,
    selectedAllCategories: false,
    update: false,
    mutation: "create",
};
const SalaryModal: React.FC<SalaryModalProps> = ({
    show,
    setShowSalaryModal,
    employeeId,
    refetch,
    t,
    toggleShowToast,
    accessMode = "hidden",
}) => {
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const { branchId } = useParams();
    const isViewOnly = accessMode === "view";

    const handleModal = () => {
        setShowSalaryModal((prevState) => !prevState);
        resetSalarySections();
    };
    const initialValues: FormValues = {
        companyId: employeeId?.id,
        salarySections: [{ categoryId: null, salary: "", salaryType: "", categoryName: "", subCategoryId: null }],
    };

    const [salarySections, setSalarySections] = useState<SalarySection[]>([
        {
            id: Date.now(),
            categoryId: null,
            salary: 0,
            salaryType: "%",
            categoryName: "",
            subCategoryName: "",
            subCategoryId: null,
            selectedAllCategories: false,
            update: false,
            salaryError: false,
            errorMessage: "",
            errorCategory: false,
            salaryErrorMessage: "",
            categoryErrorMessage: "",
        },
    ]);
    const [checkbox, setCheckbox] = useState(false);
    const [deleteState, setDeleteState] = useState([]);
    const isSelectedAll = employeeId?.ServiceCategorySalaries.some(
        (item: ServiceCategorySalaryProxy) => item.deleted === false && item.selectedAll === true
    );

    const { data: currencySign } = useGetCurrencySignQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
    });
    const currSign = currencySign?.user?.company?.currency?.currencySign;
    useEffect(() => {
        if (show) {
            setCheckbox(isSelectedAll);
        }
    }, [show, isSelectedAll]);

    useEffect(() => {
        if (show && employeeId?.ServiceCategorySalaries && currSign) {
            const transformedSalarySections = transformSalarySections(employeeId, defaultSalarySection, currSign);
            setSalarySections(transformedSalarySections);
        }
    }, [show, employeeId?.ServiceCategorySalaries, currSign, employeeId]);

    const resetSalarySections = () => {
        setSalarySections((prevSections) => {
            const selectedAll = !prevSections[0].selectedAllCategories;
            const updatedFirstSection: SalarySection = {
                ...prevSections[0],
                selectedAllCategories: selectedAll,
                errorCategory: false,
                errorMessage: "",
                salaryError: false,
                hasInteracted: false,
                categoryName: "",
                subCategoryName: "",
                categoryId: null,
                subCategoryId: null,
                salary: null,
            };
            return [updatedFirstSection];
        });
        setDeleteState([]);
    };

    const handleCheckbox = () => {
        setCheckbox((prevState) => !prevState);
        resetSalarySections();
    };
    const addSalarySection = () => {
        setSalarySections([...salarySections, { ...defaultSalarySection, id: Date.now() }]);
    };

    const [createServiceCategory] = useCreateServiceCategoryMutation({
        context: {
            errorType: "local",
        },
    });
    const [updateServiceCategory] = useUpdateServiceCategoryModalMutation({
        context: {
            errorType: "local",
        },
    });

    const [getServicesPrice] = useGetServicePriceLazyQuery();

    const handleSubmit = async () => {
        if (isViewOnly) {
            return;
        }

        try {
            const { hasErrors, updatedSections } =
                currentCompanyId &&
                (await validateSalarySections(
                    salarySections,
                    checkbox,
                    t,
                    true,
                    getServicesPrice,
                    Number(currentCompanyId)
                ));
            setSalarySections(updatedSections);

            if (hasErrors) {
                return;
            }
            await processDeleteState(
                deleteState,
                updateServiceCategory,
                setDeleteState,
                branchId,
                currentCompanyId,
                currentBranchEmployeeId
            );
            const promises = salarySections.map((section) =>
                processSalarySection(
                    section,
                    checkbox,
                    employeeId,
                    currentCompanyId,
                    branchId,
                    currentBranchEmployeeId,
                    updateServiceCategory,
                    createServiceCategory
                )
            );
            await Promise.allSettled(promises);
            await refetch();
            handleModal();
            toggleShowToast?.();
        } catch (error) {
            console.error("Error handling service category:", error);
        }
    };

    const handleSalaryValueChange = (index: number, newValue: number) => {
        const updatedSections = salarySections.map((section, i) =>
            i === index ? { ...section, salary: newValue } : section
        );
        setSalarySections(updatedSections);
    };

    const handleSalaryTypeChange = (index: number, newType: string) => {
        const updatedSections = salarySections.map((section, i) =>
            i === index ? { ...section, salaryType: newType, salaryError: false, errorMessage: "" } : section
        );
        setSalarySections(updatedSections);
    };
    const updateSalarySections = async (newSections: SalarySection[]) => {
        const { updatedSections: validatedSections } =
            validateSalarySections &&
            (await validateSalarySections(newSections, checkbox, t, false, getServicesPrice, Number(currentCompanyId)));
        setSalarySections(validatedSections);
    };
    const removeSalarySection = (id: number | string) => {
        const sectionToDelete = salarySections.find((section) => section.id === id);
        const updatedDeleteState = [...deleteState, sectionToDelete];
        const updatedSalarySections = salarySections.filter((section) => section.id !== id);
        setDeleteState(updatedDeleteState);
        updateSalarySections(updatedSalarySections);
    };

    const handleCategoryIdChange = (index: number, categoryId: number) => {
        setSalarySections((prevSections) => {
            const updatedSections = prevSections.map((section, i) =>
                i === index ? { ...section, categoryId, salaryError: false, errorMessage: "" } : section
            );
            updateSalarySections(updatedSections);
            return updatedSections;
        });
    };

    const handleSubCategoryIdChange = (index: number, subCategoryId: number) => {
        setSalarySections((prevSections) => {
            const updatedSections = prevSections.map((section, i) =>
                i === index ? { ...section, subCategoryId } : section
            );
            updateSalarySections(updatedSections);
            return updatedSections;
        });
    };

    const handleCategoryNameChange = (index: number, categoryName: string) => {
        const updatedSections = salarySections.map((section, i) =>
            i === index ? { ...section, categoryName: categoryName } : section
        );
        setSalarySections(updatedSections);
    };

    const handleSubCategoryNameChange = (index: number, subCategoryName: string) => {
        const updatedSections = salarySections.map((section, i) =>
            i === index ? { ...section, subCategoryName: subCategoryName } : section
        );
        setSalarySections(updatedSections);
    };

    const handleSalaryInteraction = (index: number) => {
        setSalarySections((prev) => {
            const updatedSections = [...prev];
            const section = updatedSections[index];

            const isSalaryValid =
                section.salaryType === "%" ? section.salary > 0 && section.salary <= 100 : section.salary > 0;

            if (isSalaryValid) {
                section.salaryError = false;
                section.errorMessage = "";
            }

            section.hasInteracted = true;
            return updatedSections;
        });
    };

    return (
        <>
            <Modal size="lg" show={show} onHide={handleModal} centered>
                <Modal.Header className="border-0 pb-0" closeButton>
                    <Col>
                        <h5 className="m-0 pb-1">
                            {t("Employee Salary")} {employeeId?.name + " " + employeeId?.surname}
                        </h5>
                    </Col>
                </Modal.Header>
                <span className="text-grey ps-3 pb-4">{t("Please specify the salary for one completed service")}</span>
                <Modal.Body className="pt-2">
                    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                        {({ isSubmitting }) => (
                            <Form>
                                {salarySections.map((section, index) => (
                                    <div key={section.id}>
                                        <Row className="pt-1">
                                            <Col sm={2} className="d-flex mb-2 mb-sm-0">
                                                <span className="text-dark fw-bold">
                                                    <small>
                                                        <label
                                                            htmlFor="salaryModal-name-field"
                                                            className="mb-0 text-dark form-label required-label"
                                                        >
                                                            {" "}
                                                            {t("Services category")}
                                                        </label>
                                                    </small>
                                                </span>
                                            </Col>
                                            <Col sm={10}>
                                                <div className="position-relative">
                                                    <div className="w-100">
                                                        {checkbox ? (
                                                            <FormCh.Group>
                                                                <FormCh.Control
                                                                    type="text"
                                                                    placeholder={t("Select a category/subcategory")}
                                                                    className="fs-7 form-control"
                                                                    disabled
                                                                    id="salaryModal-name-field"
                                                                />
                                                            </FormCh.Group>
                                                        ) : (
                                                            <CategoryInput
                                                                updateCategoryId={(id) =>
                                                                    handleCategoryIdChange(index, id)
                                                                }
                                                                updateSubCategoryId={(id) =>
                                                                    handleSubCategoryIdChange(index, id)
                                                                }
                                                                updateCategoryName={(name) =>
                                                                    handleCategoryNameChange(index, name)
                                                                }
                                                                updateSubCategoryName={(name) =>
                                                                    handleSubCategoryNameChange(index, name)
                                                                }
                                                                t={t}
                                                                nameCategory={section?.categoryName}
                                                                isValid={!!section.errorCategory}
                                                                error={section.errorCategory}
                                                                disabled={isViewOnly}
                                                            />
                                                        )}
                                                        {section.errorCategory && (
                                                            <p className="fs-7 p-1 text-danger m-0">
                                                                {section.categoryErrorMessage}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mt-3">
                                                    {salarySections.length === 1 && index === 0 && (
                                                        <FormCh.Check
                                                            className="m-0"
                                                            type={"checkbox"}
                                                            id={`default-checkbox-${index}`}
                                                            label={
                                                                <span className="fs-7 text-dark">
                                                                    {t("All Categories")}
                                                                </span>
                                                            }
                                                            checked={checkbox}
                                                            onChange={handleCheckbox}
                                                            disabled={isViewOnly}
                                                        />
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="pt-3">
                                            <Col sm={2} className="mb-2 mb-sm-0">
                                                <span className="text-dark fw-bold">
                                                    <small>
                                                        <label
                                                            htmlFor="Salary"
                                                            className="mb-0 text-dark form-label required-label"
                                                        >
                                                            {" "}
                                                            {t("Salary")}
                                                        </label>
                                                    </small>
                                                </span>
                                            </Col>
                                            <Col sm={10}>
                                                <div className="position-relative">
                                                    <div className="w-100">
                                                        <SalaryComponent
                                                            currencySign={currSign}
                                                            updateSalaryValue={(value) =>
                                                                handleSalaryValueChange(index, value)
                                                            }
                                                            updateSalaryType={(type) =>
                                                                handleSalaryTypeChange(index, type)
                                                            }
                                                            salaryError={section.salaryError}
                                                            salary={section?.salary}
                                                            currType={section?.salaryType}
                                                            onInteraction={() => handleSalaryInteraction(index)}
                                                            disabled={isViewOnly}
                                                        />
                                                        {index !== 0 && (
                                                            <Button
                                                                variant="outline-secondary"
                                                                onClick={() => removeSalarySection(section.id)}
                                                                className="text-dark ms-3 p-0"
                                                                style={{ height: "49px", width: "49px" }}
                                                                disabled={isViewOnly}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        )}
                                                        {section.salaryError && (
                                                            <p className="fs-7 p-1 text-danger m-0">
                                                                {section.salaryErrorMessage}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                        {salarySections.length > 1 && index < salarySections.length - 1 && (
                                            <Row>
                                                <Col sm={2}></Col>
                                                <Col sm={10}>
                                                    <div className="border-bottom m-0 my-4"></div>
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                ))}

                                <Row className="pt-3">
                                    <Col sm={2}></Col>
                                    <Col
                                        sm={10}
                                        className={`pt-3  ${
                                            checkbox || isViewOnly ? "text-secondary" : "text-primary"
                                        }`}
                                    >
                                        <span
                                            className="fs-7 cursor-pointer"
                                            onClick={checkbox || isViewOnly ? () => {} : addSalarySection}
                                        >
                                            + {t("Add salary for the service category")}
                                        </span>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-4 pt-2">
                                    <Button
                                        variant="outline-primary"
                                        className="fw-normal me-2 custom-btn rounded-1"
                                        onClick={handleModal}
                                        disabled={isSubmitting}
                                    >
                                        {t("Cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="fw-bold custom-btn rounded-1"
                                        disabled={isSubmitting || isViewOnly}
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                        ) : (
                                            t("save")
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SalaryModal;
